import { keysToCamelCase } from '@/services';

import {
  SDStatus,
  type TSD,
  type TSDChatItem,
  type TSDChatUpdateWs,
  type TSDChatUpdateWsResponse,
  type TSDDetailed,
  type TSDDetailedResponse,
  type TSDFilesMapItem,
  type TSDMessageDetailed,
  type TSDResponse
} from '../types/serviceDesk.type';

export const SDMapperFromResponse = (response: TSDResponse): TSD => {
  const camelCased = keysToCamelCase(response);
  return {
    ...camelCased,
    code: String(camelCased.code ?? ''),
    module: {
      ...camelCased.module,
      code: String(camelCased.module.code ?? '')
    },
    supportCategory: {
      ...camelCased.supportCategory,
      code: String(camelCased.supportCategory.code ?? '')
    }
  };
};

export const SDDetailedMapperFromResponse = (
  response: TSDDetailedResponse
): TSDDetailed => {
  const camelCased = keysToCamelCase(response);
  return {
    ...camelCased,
    code: String(camelCased.code ?? ''),
    module: {
      ...camelCased.module,
      code: String(camelCased.module.code ?? '')
    },
    supportCategory: {
      ...camelCased.supportCategory,
      code: String(camelCased.supportCategory.code ?? '')
    },
    supportChats: camelCased.supportChats.map((item) => ({
      ...item,
      code: String(item.code ?? ''),
      supportChatsFiles: item.supportChatsFiles.map((file) => file.files)
    }))
  };
};

export const SDChatUpdateWsMapperFromResponse = (
  response: TSDChatUpdateWsResponse
): TSDChatUpdateWs => {
  const camelCased = keysToCamelCase(response);
  const [uuid, message, user, status, reason, files] = camelCased;
  return {
    uuid,
    message,
    user,
    status,
    reason,
    files: files.map((item) => item.fileId)
  };
};

export const SDChatUpdateWsMapperToMessage = (
  chatUpdate: TSDChatUpdateWs
): TSDMessageDetailed => {
  const { uuid, message, user, files } = chatUpdate;
  return {
    code: String(message.code ?? ''),
    comment: message.comment,
    createdAt: message.createdAt,
    isRead: false,
    replyMsgUuid: message.replyMsgUuid || null,
    supportChatsFiles: files,
    user: {
      firstName: user.firstName,
      lastName: user.lastName,
      middleName: user.middleName,
      uuid: user.uuid
    },
    userType: message.userType,
    uuid
  };
};

export const SDMessagesMapperToChatItems = (
  request: TSDDetailed,
  chatFilesMap: Record<string, TSDFilesMapItem | undefined>
): [TSDChatItem[], { uuid: string; fileName: string }[]] => {
  const newMessagesWithFiles: { uuid: string; fileName: string }[] = [];

  const initialMessage: TSDMessageDetailed = {
    supportChatsFiles: [],
    code: 'INITIAL_MESSAGE',
    comment: request.comment,
    createdAt: request.createdAt,
    isRead: false,
    replyMsgUuid: null,
    userType: 'user',
    uuid: 'INITIAL_MESSAGE',
    user: {
      firstName: request.createUser.firstName,
      lastName: request.createUser.lastName,
      middleName: request.createUser.middleName,
      uuid: request.createUser.uuid
    }
  };

  const messages: TSDChatItem[] = [...request.supportChats, initialMessage].map(
    (item) => {
      if (item.supportChatsFiles.length > 0 && !chatFilesMap[item.uuid]) {
        item.supportChatsFiles.forEach((file) => {
          if (chatFilesMap[file?.uuid || ''] === undefined) {
            newMessagesWithFiles.push({
              uuid: file?.uuid || '',
              fileName: file?.name || ''
            });
          }
        });
      }
      return {
        item,
        createdAt: item.createdAt,
        index: -1,
        isRead: !!item.isRead,
        replyIndex: -1,
        type: 'message',
        userType: item.userType,
        uuid: item.uuid
      };
    }
  );

  return [messages, newMessagesWithFiles];
};

export const SDStatusChangesMapperToChatItems = (
  request: TSDDetailed
): TSDChatItem[] => {
  return request.supportReasonsHistory.slice().map((item) => ({
    item,
    createdAt: item.createdAt,
    index: -1,
    isRead: !!item.isRead,
    replyIndex: -1,
    type: 'status_change',
    userType:
      item.supportReasons?.type ??
      (item.status.code === SDStatus.inProgress ||
      item.status.code === SDStatus.completed
        ? 'admin'
        : 'user'),
    uuid: item.uuid
  }));
};
